import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { Animated } from 'react-native';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { CoreConfig } from '../../../CoreConfig';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { useAnimation } from '../Animations/AnimationHooks';
import { getColorScheme } from '../../Hooks/UseColorScheme';
import { getDirection } from '../../Hooks/UseDirection';
const keys = {
    disabled: 0,
    default: 1,
    hover: 2,
    checked: 3
};
export const SwitchView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.switch,
    style(theme, mediaQuery, window, props) {
        var _a, _b, _c;
        return {
            content: {
                backgroundColor: getColorScheme().value.colors.background || '#fff'
            },
            container: {
                width: 40,
                height: 20,
                borderRadius: 10,
                borderWidth: 2,
                borderStyle: 'solid',
                padding: 2
            },
            default: {
                backgroundColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.link_color) || 'rgb(99, 120, 143)'
            },
            hover: {
                backgroundColor: ((_b = theme.colors) === null || _b === void 0 ? void 0 : _b.secondary_color) || 'rgba(17, 85, 204, 1)'
            },
            active: {
                backgroundColor: ((_c = theme.colors) === null || _c === void 0 ? void 0 : _c.secondary_color) || 'rgba(17, 85, 204, 1)'
            },
            activePan: {
                backgroundColor: '#fff'
            },
            disabled: {
                backgroundColor: 'rgb(191, 204, 218)'
            },
            pan: {
                width: 12,
                height: 12,
                borderRadius: 6,
                transform: [
                    {
                        translateX: theme.useSimpeAnimatedValue('translateX', props.value ? getDirection().value === 'rtl' ? -20 : 20 : 0)
                    }
                ]
            }
        };
    }
})(({ style, value, disabled, testID, onChange }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const [borderColor, setBorderColor] = useAnimation({ initialValue: keys.default, nativeDriver: false });
    const [panColor, setPanColor] = useAnimation({ initialValue: keys.default, nativeDriver: false });
    const [backgroundColor, setBackgroundColor] = useAnimation({ initialValue: keys.default, nativeDriver: false });
    const handlePress = useCallback(() => {
        if (disabled) {
            return;
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(!value);
    }, [value, onChange, disabled]);
    useEffect(() => {
        if (disabled) {
            setBorderColor(keys.disabled);
            setPanColor(keys.disabled);
            setBackgroundColor(keys.disabled);
            return;
        }
        if (value) {
            setBorderColor(keys.checked);
            setPanColor(keys.checked);
            setBackgroundColor(keys.checked);
            return;
        }
        setBorderColor(keys.default);
        setPanColor(keys.default);
        setBackgroundColor(keys.default);
    }, [value, disabled]);
    const handleHover = useCallback(() => {
        if (disabled) {
            return;
        }
        setBorderColor(keys.hover);
        setPanColor(keys.hover);
        setBackgroundColor(keys.hover);
    }, [disabled]);
    const handleBlur = useCallback(() => {
        if (disabled) {
            return;
        }
        if (value) {
            setBorderColor(keys.checked);
            setPanColor(keys.checked);
            setBackgroundColor(keys.checked);
            return;
        }
        setBorderColor(keys.default);
        setPanColor(keys.default);
        setBackgroundColor(keys.default);
    }, [disabled, value]);
    const animatedBorderColor = borderColor.interpolate({
        inputRange: [keys.disabled, keys.default, keys.hover, keys.checked],
        outputRange: [
            (_a = style === null || style === void 0 ? void 0 : style.disabled) === null || _a === void 0 ? void 0 : _a.backgroundColor,
            (_b = style === null || style === void 0 ? void 0 : style.default) === null || _b === void 0 ? void 0 : _b.backgroundColor,
            (_c = style === null || style === void 0 ? void 0 : style.hover) === null || _c === void 0 ? void 0 : _c.backgroundColor,
            (_d = style === null || style === void 0 ? void 0 : style.active) === null || _d === void 0 ? void 0 : _d.backgroundColor
        ]
    });
    const animatedPanColor = panColor.interpolate({
        inputRange: [keys.disabled, keys.default, keys.hover, keys.checked],
        outputRange: [
            (_e = style === null || style === void 0 ? void 0 : style.disabled) === null || _e === void 0 ? void 0 : _e.backgroundColor,
            (_f = style === null || style === void 0 ? void 0 : style.default) === null || _f === void 0 ? void 0 : _f.backgroundColor,
            (_g = style === null || style === void 0 ? void 0 : style.hover) === null || _g === void 0 ? void 0 : _g.backgroundColor,
            (_h = style === null || style === void 0 ? void 0 : style.activePan) === null || _h === void 0 ? void 0 : _h.backgroundColor
        ]
    });
    const animatedBackgroundColor = backgroundColor.interpolate({
        inputRange: [keys.disabled, keys.default, keys.hover, keys.checked],
        outputRange: [
            (_j = style === null || style === void 0 ? void 0 : style.content) === null || _j === void 0 ? void 0 : _j.backgroundColor,
            (_k = style === null || style === void 0 ? void 0 : style.content) === null || _k === void 0 ? void 0 : _k.backgroundColor,
            (_l = style === null || style === void 0 ? void 0 : style.content) === null || _l === void 0 ? void 0 : _l.backgroundColor,
            (_m = style === null || style === void 0 ? void 0 : style.active) === null || _m === void 0 ? void 0 : _m.backgroundColor
        ]
    });
    return (_jsx(AnimatedPressable, { testID: testID, style: [style === null || style === void 0 ? void 0 : style.container, { borderColor: animatedBorderColor, backgroundColor: animatedBackgroundColor }], onPress: handlePress, disabled: disabled, onMouseEnter: handleHover,
        onMouseLeave: handleBlur, children: _jsx(Animated.View, { style: [style === null || style === void 0 ? void 0 : style.pan, { backgroundColor: animatedPanColor }] }) }));
});
